import { useEffect } from 'react';
import { inputService } from '../services/InputService';
import { dungeonService } from '../services/DungeonService';
import { useGameStore } from '../store/gameSlice';
import {
  getDirectionFromPositions,
  getTargetPosition,
} from '../utils/map.utils';
import { DirectionEnum, DungeonEntityType } from '../types/enums';
import { PlayerEntity } from '../entities/PlayerEntity';
import { registerDungeonInput } from './useGameInput';
import { useInputContext } from './useInput';
import { DirectionInput, resolveDirection } from '../utils/input.utils';
import { openInterface } from '../services/InterfaceService';
import { spellExecutor } from '../services/SpellService';
import { useCinematicStore } from '../store/cinematicSlice';
import { useControlsStore } from '../store/controlsSlice';
import { mouseAimService } from '../services/MouseAimService';
import { netService } from '../services/NetService';
import {
  sendIntent,
  sendMoveIntent,
  pendingMoveCount,
  nextCastSeq,
} from '../net/inputSync';
import { SPELL_REGISTRY } from '../data/spells.config';
import { useSpectatorStore } from '../store/spectatorSlice';
import { getClientToken } from '../net/reconnect';
import { makePlayerLoadout } from '../net/loadoutSync';
import { captureLoadoutSnapshot } from '../services/LoadoutSourceService';
import {
  getMasteryLevelLive,
  gainMasteryXp,
} from '../services/SpellMasteryService';
import { LOCATION_REGISTRY } from '../data/locations.config';
import { useInventoryStore } from '../store/inventorySlice';

function tryLocationInteraction(): void {
  const location = dungeonService.locationService.getInteractableLocation();
  if (!location) {
    return;
  }
  const locDef = LOCATION_REGISTRY[location.id];
  if (netService.isClient()) {
    if (locDef?.requiresKey) {
      const inv = useInventoryStore.getState();
      const hasKey = inv.slots.some(
        (s) => s?.itemId === 'key' && s.quantity > 0,
      );
      if (!hasKey) {
        return;
      }
      inv.removeItem('key', 1);
    }
    sendIntent({ type: 'interact-intent' });
    return;
  }
  dungeonService.locationService.interactWithLocation(location);
}

/** Client-only: the host executes our cast but must not credit our mastery, so
 *  we accrue locally (like solo) and re-sync the snapshot when a level changes. */
function accrueClientMastery(spellId: string): void {
  if (!netService.isClient()) {
    return;
  }
  const before = getMasteryLevelLive(spellId);
  gainMasteryXp(spellId, 1);
  if (getMasteryLevelLive(spellId) !== before) {
    netService.session?.broadcast(
      'reliable',
      makePlayerLoadout(captureLoadoutSnapshot(), getClientToken()),
    );
  }
}

/** Client-only: locally predict a slot cast — block movement for its cast/action/
 *  lag phases, predict its cooldown, accrue mastery. The host runs the real spell;
 *  we only mirror the timing so `canMove` honors the lag (else a move pressed
 *  during the lag is predicted and rubber-bands when the host rejects it). */
function predictClientCast(
  player: PlayerEntity,
  slot: number,
  dir: DirectionEnum,
  castSeq: number,
): void {
  const spellId = player.slots[slot]?.spellId;
  if (!spellId) {
    return;
  }
  const def = player.getSpellInstance(spellId) ?? SPELL_REGISTRY[spellId];
  if (
    !def ||
    !player.canCast() ||
    player.getSpellState(def.id).cooldownRemaining > 0
  ) {
    return;
  }
  spellExecutor.predictCastBlocking(player, def);
  player.predictSpellCooldown(def);
  accrueClientMastery(def.id);
  const target = getTargetPosition(player.positionX, player.positionY, dir);
  spellExecutor.predictPreviewTiles(player, def, castSeq, target.x, target.y);
}

function tryBasicAttack(
  player: PlayerEntity,
  direction: DirectionEnum | null,
): void {
  const dir = direction ?? player.direction;
  if (netService.isClient()) {
    const def = player.getBasicAttackSpell();
    const seq = nextCastSeq();
    if (
      player.canCast() &&
      player.getSpellState(def.id).cooldownRemaining <= 0
    ) {
      spellExecutor.predictCastBlocking(player, def);
      player.predictSpellCooldown(def);
      accrueClientMastery(def.id);
      const target = getTargetPosition(player.positionX, player.positionY, dir);
      spellExecutor.predictPreviewTiles(player, def, seq, target.x, target.y);
    }
    sendIntent({ type: 'attack-intent', dir, seq });
    return;
  }
  if (!player.canCast()) {
    return;
  }
  const target = getTargetPosition(player.positionX, player.positionY, dir);
  player.basicAttack(target.x, target.y);
}

type SkillSlotFn = (
  player: PlayerEntity,
  targetX: number,
  targetY: number,
) => void;

/** SKILL_(N+1) → slot N's spell (4 fixed slots; cf. PlayerEntity.slots). */
function getSkillSlotFn(index: number): SkillSlotFn {
  return (player, tx, ty) => {
    if (index >= player.slots.length) {
      return;
    }
    const spellId = player.slots[index].spellId;
    if (!spellId) {
      return;
    }
    spellExecutor.execute(player, spellId, tx, ty);
  };
}

const SKILL_SLOTS: SkillSlotFn[] = [
  getSkillSlotFn(0),
  getSkillSlotFn(1),
  getSkillSlotFn(2),
  getSkillSlotFn(3),
];

const SKILL_ACTIONS = ['SKILL_1', 'SKILL_2', 'SKILL_3', 'SKILL_4'] as const;

function readDirection(): DirectionEnum | null {
  const lockDiagonal = inputService.isActionActive('dungeon', 'LOCK_DIAGONAL');
  const dirInput: DirectionInput = {
    up: inputService.isActionActive('dungeon', 'MOVE_UP'),
    down: inputService.isActionActive('dungeon', 'MOVE_DOWN'),
    left: inputService.isActionActive('dungeon', 'MOVE_LEFT'),
    right: inputService.isActionActive('dungeon', 'MOVE_RIGHT'),
  };
  return resolveDirection(dirInput, lockDiagonal);
}

function executeFace(player: PlayerEntity): void {
  const px = player.positionX;
  const py = player.positionY;
  let nearest: { dist: number; dir: DirectionEnum } | null = null;

  for (const entity of dungeonService.getEntities()) {
    if (entity.type !== DungeonEntityType.MONSTER) {
      continue;
    }
    const dx = entity.positionX - px;
    const dy = entity.positionY - py;
    if (Math.abs(dx) <= 1 && Math.abs(dy) <= 1 && (dx !== 0 || dy !== 0)) {
      const dist = Math.abs(dx) + Math.abs(dy);
      if (!nearest || dist < nearest.dist) {
        nearest = {
          dist,
          dir: getDirectionFromPositions(
            px,
            py,
            entity.positionX,
            entity.positionY,
          ),
        };
      }
    }
  }

  if (nearest) {
    if (netService.isClient()) {
      sendIntent({ type: 'face-intent', dir: nearest.dir });
    }
    player.face(nearest.dir);
  }
}

function cycleWatchedAlly(): void {
  const allies = dungeonService
    .getPlayers()
    .filter((p) => p.id !== dungeonService.localPlayerId);
  if (allies.length === 0) {
    return;
  }
  const cur = useSpectatorStore.getState().watchedAllyId;
  const idx = allies.findIndex((p) => p.id === cur);
  const next = allies[(idx + 1) % allies.length];
  useSpectatorStore.getState().setWatchedAlly(next.id);
}

// Input buffer: stores the last action pressed while blocked, replayed on unblock.
const INPUT_BUFFER_DURATION_MS = 300;

// Client prediction cap: max move intents sent but not yet acked by the host
// before the client stops predicting further steps. Bounds desync from a step
// the host rejects (it can't run more than this many cells ahead).
const MAX_PENDING_MOVES = 2;

type BufferedAction =
  | { type: 'face' }
  | { type: 'basicAttack' }
  | { type: 'interact' }
  | { type: 'skill'; index: number };

let bufferedAction: BufferedAction | null = null;
let bufferTimestamp = 0;

function resetDungeonInputState(): void {
  bufferedAction = null;
  bufferTimestamp = 0;
}

function bufferInput(action: BufferedAction): void {
  bufferedAction = action;
  bufferTimestamp = performance.now();
}

function consumeBuffer(): BufferedAction | null {
  if (!bufferedAction) {
    return null;
  }
  if (performance.now() - bufferTimestamp > INPUT_BUFFER_DURATION_MS) {
    bufferedAction = null;
    return null;
  }
  const action = bufferedAction;
  bufferedAction = null;
  return action;
}

export function useDungeonInput() {
  useInputContext('dungeon', {
    onSuspended: () => {
      resetDungeonInputState();
      mouseAimService.reset();
    },
  });

  useEffect(() => {
    const process = () => {
      const { gameplay } = useGameStore.getState();

      if (gameplay !== 'dungeon') {
        return;
      }

      if (useCinematicStore.getState().active) {
        return;
      }

      // Downed: no local entity exists, so this must gate BEFORE the player
      // null-check below — otherwise the ally-cycle key would never fire.
      if (useSpectatorStore.getState().downedSelf) {
        if (inputService.isActionJustPressed('dungeon', 'INTERACT')) {
          cycleWatchedAlly();
        }
        return;
      }

      if (inputService.isActionJustPressed('dungeon', 'INVENTORY')) {
        openInterface('inventory');
      }
      if (inputService.isActionJustPressed('dungeon', 'OPEN_MAP')) {
        openInterface('map');
      }

      const player = dungeonService.getPlayer() as PlayerEntity | undefined;
      if (!player) {
        return;
      }

      const kmActive = useControlsStore.getState().mouseControlEnabled;
      const aimDir = kmActive ? mouseAimService.getAimDirection(player) : null;

      // While blocked, capture the latest action into the buffer. Blocked = the
      // global gate is closed (floor transition / not yet initialized) OR the
      // local player is mid blocking-action (per-player, so co-op players don't
      // block each other).
      if (!dungeonService.allowPlayerInput || !player.allowInput) {
        if (inputService.isActionJustPressed('dungeon', 'FACE')) {
          bufferInput({ type: 'face' });
        }
        for (let i = 0; i < SKILL_ACTIONS.length; i++) {
          if (inputService.isActionJustPressed('dungeon', SKILL_ACTIONS[i])) {
            bufferInput({ type: 'skill', index: i });
            break;
          }
        }
        if (inputService.isActionJustPressed('dungeon', 'BASIC_ATTACK')) {
          bufferInput({ type: 'basicAttack' });
        }
        // INTERACT may overwrite a buffered attack pressed on the same frame —
        // fine: a single action is replayed on unblock, and interact is the
        // most recent intent.
        if (inputService.isActionJustPressed('dungeon', 'INTERACT')) {
          bufferInput({ type: 'interact' });
        }
        if (mouseAimService.consumeLeftClick()) {
          bufferInput({ type: 'basicAttack' });
        }
        if (mouseAimService.consumeRightClick()) {
          bufferInput({ type: 'interact' });
        }
        return;
      }

      const buffered = consumeBuffer();
      if (buffered) {
        const direction = readDirection();
        const actionDir = kmActive ? aimDir : direction;
        switch (buffered.type) {
          case 'face':
            executeFace(player);
            return;
          case 'skill': {
            if (netService.isClient()) {
              const seq = nextCastSeq();
              const dir = (actionDir ?? player.direction) as DirectionEnum;
              predictClientCast(player, buffered.index, dir, seq);
              sendIntent({
                type: 'skill-intent',
                slot: buffered.index,
                dir: dir as number,
                seq,
              });
              return;
            }
            const fn = SKILL_SLOTS[buffered.index];
            if (fn && player.canCast()) {
              const target = getTargetPosition(
                player.positionX,
                player.positionY,
                actionDir ?? player.direction,
              );
              fn(player, target.x, target.y);
              return;
            }
            break;
          }
          case 'basicAttack':
            tryBasicAttack(player, actionDir);
            return;
          case 'interact':
            tryLocationInteraction();
            return;
        }
      }

      const faceHeld = inputService.isActionActive('dungeon', 'FACE');

      // Auto-face: a bare FACE press aims at the nearest adjacent enemy.
      if (inputService.isActionJustPressed('dungeon', 'FACE')) {
        executeFace(player);
      }

      const direction = readDirection();
      const actionDir = kmActive ? aimDir : direction;

      for (let i = 0; i < SKILL_ACTIONS.length; i++) {
        if (inputService.isActionJustPressed('dungeon', SKILL_ACTIONS[i])) {
          if (netService.isClient()) {
            const seq = nextCastSeq();
            const dir = (actionDir ?? player.direction) as DirectionEnum;
            predictClientCast(player, i, dir, seq);
            sendIntent({
              type: 'skill-intent',
              slot: i,
              dir: dir as number,
              seq,
            });
            return;
          }
          const fn = SKILL_SLOTS[i];
          if (fn && player.canCast()) {
            const target = getTargetPosition(
              player.positionX,
              player.positionY,
              actionDir ?? player.direction,
            );
            fn(player, target.x, target.y);
            return;
          }
        }
      }

      // Independent: if the user bound both to the same key, both fire (intentional).
      const interactPressed = inputService.isActionJustPressed(
        'dungeon',
        'INTERACT',
      );
      const attackPressed = inputService.isActionJustPressed(
        'dungeon',
        'BASIC_ATTACK',
      );
      if (interactPressed) {
        tryLocationInteraction();
      }
      if (attackPressed) {
        tryBasicAttack(player, actionDir);
      }
      if (interactPressed || attackPressed) {
        return;
      }

      const leftClick = mouseAimService.consumeLeftClick();
      const rightClick = mouseAimService.consumeRightClick();
      if (rightClick) {
        tryLocationInteraction();
      }
      if (leftClick) {
        tryBasicAttack(player, actionDir);
      }
      if (leftClick || rightClick) {
        return;
      }

      // Idle: face the cursor (km only). Guarded on change so a still player
      // aiming the same way is not re-faced every frame.
      if (
        kmActive &&
        aimDir !== null &&
        aimDir !== player.direction &&
        direction === null
      ) {
        if (netService.isClient()) {
          sendIntent({ type: 'face-intent', dir: aimDir });
        }
        player.face(aimDir);
      }

      if (direction !== null) {
        if (faceHeld) {
          // FACE held + direction = turn in place without moving.
          if (netService.isClient()) {
            sendIntent({ type: 'face-intent', dir: direction });
          }
          player.face(direction);
        } else if (player.canMove()) {
          // Client: hold off predicting once too many steps are unacked, so a
          // step the host rejects can't run the prediction far ahead.
          if (
            netService.isClient() &&
            pendingMoveCount() >= MAX_PENDING_MOVES
          ) {
            return;
          }
          const target = getTargetPosition(
            player.positionX,
            player.positionY,
            direction,
          );
          if (netService.isClient()) {
            sendMoveIntent(direction);
          }
          // Turn anyway if the move fails (wall, entity).
          if (!player.move(target.x, target.y)) {
            player.face(direction);
          }
        }
      }
    };

    registerDungeonInput(process);
    return () => registerDungeonInput(null);
  }, []);
}
